import type { PlaylistItem } from '../../types.js';
import { getClipPathPolygon } from '../../utils/clip_path_polygon.js';
import { BasePlayer, type BasePlayerOptions } from '../base_player.js';

import style from '../player.module.css';

export type CompareAwarePlayerOptions = BasePlayerOptions<HTMLElement> & {
  isComparePlayer?: boolean;
};

export abstract class CompareAwarePlayer extends BasePlayer<HTMLElement> {
  mediaElement:
    | HTMLMediaElement
    | HTMLImageElement
    | HTMLDivElement
    | undefined;

  mediaWrapper: HTMLDivElement | undefined;

  isComparePlayer: boolean | undefined;

  protected constructor(options: CompareAwarePlayerOptions) {
    super(options);
    this.isComparePlayer = options.isComparePlayer;

    if (options.isComparePlayer) {
      return;
    }
    this.subscribersList.subscribe(
      [
        this.store.compare.$compareActive,
        this.store.compare.$compareItem,
        this.store.compare.$compareMode,
      ],
      this.#handleCompareMode,
    );
    this.subscribersList.listen(
      this.store.compare.$overlayOpacity,
      this.#handleOverlayOpacity,
    );
    this.subscribersList.listen(this.store.compare.$wipe, this.#handleWipe);
  }

  isMediaElementInitialized(): this is { mediaElement: HTMLMediaElement } {
    return Boolean(this.mediaElement);
  }

  protected get compareStyleTarget(): HTMLElement | undefined {
    return this.mediaWrapper ?? this.mediaElement;
  }

  async init(): Promise<void> {
    await super.init();
    if (!this.isMediaElementInitialized()) {
      return;
    }

    // Wrap the media element to allow for side by side compare to work with zoom/pan
    const mediaWrapper = document.createElement('div');
    if (
      this.playerType === 'image' ||
      this.playerType === 'video' ||
      this.playerType === 'stream'
    ) {
      mediaWrapper.classList.add(style.media_wrapper);
    }
    mediaWrapper.appendChild(this.mediaElement);
    this.mediaWrapper = mediaWrapper;

    if (this.isComparePlayer) {
      this.containerElement.prepend(mediaWrapper);
    } else {
      this.containerElement.append(mediaWrapper);
    }
  }

  async destroy() {
    await super.destroy();
    if (!this.isComparePlayer) {
      this.store.playableMedia.setCurrentSubtitlesId(undefined);
      this.store.setLoadState(undefined);
    }
    this.mediaElement?.remove();
    this.mediaElement = undefined;
    this.mediaWrapper?.remove();
    this.mediaWrapper = undefined;
  }

  abstract load(playlistItem: PlaylistItem): Promise<void>;

  play(): void {}

  pause(): void {}

  #handleCompareMode = () => {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    this.#handleOverlayOpacity();
    this.#handleWipe();
    const isSideBySide = this.store.compare.$compareMode.get() === 'sideBySide';
    const shouldCompare =
      this.store.compare.$compareAvailable.get() &&
      this.store.compare.$compareActive.get();
    // Use mediaWrapper directly so players that insert an extra container
    // between mediaWrapper and mediaElement (e.g. StreamPlayer's div.video-js)
    // still get position applied to the right node.
    if (!this.mediaWrapper) {
      return;
    }
    if (isSideBySide || !shouldCompare) {
      this.mediaWrapper.style.position = '';
      return;
    }

    this.mediaWrapper.style.position = 'absolute';
  };

  #handleOverlayOpacity = () => {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    const shouldCompare =
      this.store.compare.$compareAvailable.get() &&
      this.store.compare.$compareActive.get();
    const isOverlayCompareActive =
      this.store.compare.$compareMode.get() === 'overlay';
    const opacityTarget = this.compareStyleTarget;
    if (!opacityTarget) {
      return;
    }
    if (!shouldCompare || !isOverlayCompareActive) {
      opacityTarget.style.opacity = '';
      return;
    }

    const opacity = this.store.compare.$overlayOpacity.get();
    opacityTarget.style.opacity = opacity.toString();
  };

  #handleWipe = () => {
    if (!this.isMediaElementInitialized()) {
      return;
    }
    const shouldCompare =
      this.store.compare.$compareAvailable.get() &&
      this.store.compare.$compareActive.get();
    const isWipeCompareActive =
      this.store.compare.$compareMode.get() === 'wipe';
    const clipTarget = this.compareStyleTarget;
    if (!clipTarget) {
      return;
    }
    if (!shouldCompare || !isWipeCompareActive) {
      clipTarget.style.clipPath = '';
      return;
    }
    const wipe = this.store.compare.$wipe.get();

    // No aspect ratio conversion needed - angle is stored as normalized [0..1] value
    // representing screen-space rotation, which works directly across all platforms
    clipTarget.style.clipPath = getClipPathPolygon(wipe.x, wipe.y, wipe.angle);
  };
}
